import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';

export type AIAgent = 'anna' | 'eva';

export interface BarInnerProps {
  mode: string;
  isMobile: boolean;
  autoFocus?: boolean;
  externalInput?: string;
  onExternalInputConsumed?: () => void;
  onSend: (message: string, files?: File[]) => void;
  isStreaming?: boolean;
  onStop?: () => void;
  hideMode?: boolean;
  currentAgent?: AIAgent;
  onAgentChange?: (agent: AIAgent) => void;
  conversationId?: string | null;
  userId?: string | null;
  customPlaceholder?: string;
  onVoiceCall?: () => void;
}

const agentConfig: Record<AIAgent, { name: string; role: string }> = {
  anna: { name: 'anna', role: 'Sovereign Counterpart' },
  eva:  { name: 'eva',  role: 'Tactical Engine' },
};

interface AttachedFile {
  file: File;
  preview: string;
  id: string;
}

// ─── Shared inner bar ─────────────────────────────────────────────────────────
export function BarInner({
  mode,
  isMobile,
  autoFocus = false,
  externalInput = '',
  onExternalInputConsumed,
  onSend,
  isStreaming = false,
  onStop,
  currentAgent = 'anna',
  onAgentChange,
  conversationId,
  userId,
  customPlaceholder,
  onVoiceCall,
}: BarInnerProps) {
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  const [input, setInput] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [micActive, setMicActive] = useState(false);
  const recognitionRef = useRef<any>(null);

  const [taskCommand, setTaskCommand] = useState<{
    type: string;
    processor: 'eva' | 'gemini' | 'grok';
    priority: 'low' | 'normal' | 'high' | 'urgent';
    payload: string;
  } | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const pillRef = useRef<HTMLDivElement>(null);

  const hasContent = input.trim().length > 0 || attachedFiles.length > 0;
  const [pillHeight, setPillHeight] = useState(56);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollH = textareaRef.current.scrollHeight;
      const newTextH = Math.min(scrollH, 120);
      textareaRef.current.style.height = `${newTextH}px`;
      const extraLines = Math.max(0, newTextH - 30);
      setPillHeight(Math.min(56 + extraLines, 56 + 96));
    }
  }, [input]);

  const focusTextarea = useCallback(() => {
    setTimeout(() => {
      if (textareaRef.current && document.activeElement !== textareaRef.current) {
        textareaRef.current.focus({ preventScroll: true });
      }
    }, 0);
  }, []);

  useEffect(() => {
    if (externalInput) {
      setInput(externalInput);
      onExternalInputConsumed?.();
      setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 50);
    }
  }, [externalInput, onExternalInputConsumed]);

  useEffect(() => {
    const resetDrag = () => { dragCounterRef.current = 0; setIsDragging(false); };
    window.addEventListener('dragend', resetDrag);
    window.addEventListener('drop', resetDrag);
    return () => { window.removeEventListener('dragend', resetDrag); window.removeEventListener('drop', resetDrag); };
  }, []);

  const handleSubmit = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed && attachedFiles.length === 0) return;
    let messageToSend = trimmed;
    if (trimmed.length > 3000) {
      try {
        const charLength = trimmed.length;
        const preview = trimmed.slice(0, 80).replace(/\n/g, ' ');
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        const { data: attachment, error } = await supabase
          .from('message_attachments')
          .insert({ user_id: currentUser?.id ?? userId ?? null, conversation_id: conversationId ?? null, content: trimmed, char_length: charLength })
          .select('id')
          .maybeSingle();
        if (!error && attachment) {
          messageToSend = `[ATTACHMENT: ${attachment.id} — ${charLength} chars — ${preview}...]`;
        }
      } catch {
        messageToSend = trimmed.slice(0, 2800) + '\n\n[Message truncated — attachment storage unavailable]';
      }
    }
    if (micActive) { recognitionRef.current?.stop(); setMicActive(false); }
    onSend(messageToSend, attachedFiles.map(af => af.file));
    setInput('');
    setAttachedFiles([]);
    setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 30);
  }, [input, attachedFiles, onSend, conversationId, userId, micActive]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    const newFiles: AttachedFile[] = Array.from(files).map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
      id: `${Date.now()}-${Math.random()}`,
    }));
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (id: string) => {
    setAttachedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    dragCounterRef.current = 0; setIsDragging(false);
    if (e.dataTransfer.files?.length) handleFileSelect(e.dataTransfer.files);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleInputChange = useCallback((newVal: string) => {
    setInput(newVal);
    if (newVal.trimStart() === '/task' && taskCommand === null) {
      setTaskCommand({ type: '', processor: 'eva', priority: 'normal', payload: '' });
    } else if (!newVal.trimStart().startsWith('/task') && taskCommand !== null) {
      setTaskCommand(null);
    }
  }, [taskCommand]);

  const handleDispatchTask = useCallback(() => {
    if (!taskCommand) return;
    const { type, processor, priority, payload } = taskCommand;
    if (!type.trim()) { textareaRef.current?.focus(); return; }
    const msg = `/task type="${type.trim()}" processor=${processor} priority=${priority}${payload.trim() ? ` payload="${payload.trim()}"` : ''}`;
    setTaskCommand(null); setInput('');
    onSend(msg);
    setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 30);
  }, [taskCommand, onSend]);

  const dismissTaskCommand = useCallback(() => {
    setTaskCommand(null); setInput('');
    setTimeout(() => textareaRef.current?.focus({ preventScroll: true }), 30);
  }, []);

  const currentAgentConfig = agentConfig[currentAgent];

  return (
    <>
    <div className="flex flex-col gap-2">
      {/* /task command panel */}
      <AnimatePresence>
        {taskCommand !== null && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
            style={{
              background: 'rgba(8,8,10,0.97)',
              border: '1px solid rgba(201,169,110,0.22)',
              borderRadius: 16,
              padding: '16px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.6), 0 0 0 0.5px rgba(201,169,110,0.08) inset',
            }}
          >
            <div className="absolute inset-x-0 top-0 h-px rounded-t-2xl" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,169,110,0.4) 50%, transparent)' }} />
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: 'rgba(201,169,110,0.1)', border: '0.5px solid rgba(201,169,110,0.25)' }}>
                <i className="ri-flashlight-line" style={{ fontSize: 12, color: 'rgba(201,169,110,0.9)' }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(201,169,110,0.8)' }}>
                Dispatch Task
              </span>
              <button onClick={dismissTaskCommand} className="ml-auto w-5 h-5 flex items-center justify-center rounded-lg cursor-pointer transition-opacity hover:opacity-70"
                style={{ background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.35)' }}>
                <i className="ri-close-line text-xs" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block mb-1" style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)' }}>Task type</label>
                <input autoFocus value={taskCommand.type}
                  onChange={e => setTaskCommand(prev => prev ? { ...prev, type: e.target.value } : prev)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleDispatchTask(); } if (e.key === 'Escape') dismissTaskCommand(); }}
                  placeholder="e.g. research, memory_audit, code_review, analysis…"
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.8)', outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block mb-1" style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)' }}>Processor</label>
                  <select value={taskCommand.processor} onChange={e => setTaskCommand(prev => prev ? { ...prev, processor: e.target.value as 'eva' | 'gemini' | 'grok' } : prev)}
                    style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.8)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                    <option value="eva" style={{ background: '#0a0a0a' }}>EVA</option>
                    <option value="gemini" style={{ background: '#0a0a0a' }}>Gemini</option>
                    <option value="grok" style={{ background: '#0a0a0a' }}>Grok</option>
                    <option value="claude" style={{ background: '#0a0a0a' }}>Claude</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1" style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)' }}>Priority</label>
                  <select value={taskCommand.priority} onChange={e => setTaskCommand(prev => prev ? { ...prev, priority: e.target.value as 'low' | 'normal' | 'high' | 'urgent' } : prev)}
                    style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.8)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}>
                    <option value="low" style={{ background: '#0a0a0a' }}>Low</option>
                    <option value="normal" style={{ background: '#0a0a0a' }}>Normal</option>
                    <option value="high" style={{ background: '#0a0a0a' }}>High</option>
                    <option value="urgent" style={{ background: '#0a0a0a' }}>Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block mb-1" style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)' }}>Payload description</label>
                <textarea value={taskCommand.payload} onChange={e => setTaskCommand(prev => prev ? { ...prev, payload: e.target.value } : prev)}
                  onKeyDown={e => { if (e.key === 'Escape') dismissTaskCommand(); }}
                  placeholder="Describe what the processor should do…" rows={2}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.8)', outline: 'none', fontFamily: 'inherit', resize: 'none', lineHeight: 1.5 }}
                />
              </div>
              <button onClick={handleDispatchTask} disabled={!taskCommand.type.trim()}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl cursor-pointer transition-all whitespace-nowrap"
                style={{ background: taskCommand.type.trim() ? 'rgba(201,169,110,0.15)' : 'rgba(255,255,255,0.04)', border: `0.5px solid ${taskCommand.type.trim() ? 'rgba(201,169,110,0.35)' : 'rgba(255,255,255,0.07)'}`, color: taskCommand.type.trim() ? 'rgba(201,169,110,1)' : 'rgba(255,255,255,0.25)', fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', opacity: taskCommand.type.trim() ? 1 : 0.6 }}>
                <i className="ri-send-plane-line text-sm" />
                Dispatch to {taskCommand.processor}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* File Preview Strip */}
      <AnimatePresence>
        {attachedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 8, height: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className="overflow-x-auto overflow-y-hidden rounded-2xl border p-2"
            style={{ background: 'var(--color-surface-raised)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex gap-2">
              {attachedFiles.map(file => (
                <motion.div key={file.id} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                  className="relative flex-shrink-0 rounded-lg overflow-hidden group"
                  style={{ width: file.preview ? '80px' : 'auto', height: '80px', border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
                  {file.preview ? (
                    <img src={file.preview} alt={file.file.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center px-3 gap-1">
                      <i className="ri-file-line text-2xl" style={{ color: 'var(--color-text-tertiary)' }} />
                      <span className="text-[10px] text-center truncate w-full" style={{ color: 'var(--color-text-tertiary)' }}>{file.file.name}</span>
                      <span className="text-[9px]" style={{ color: 'var(--color-text-tertiary)' }}>{formatFileSize(file.file.size)}</span>
                    </div>
                  )}
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleRemoveFile(file.id)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }} aria-label="Remove file">
                    <i className="ri-close-line text-xs leading-none" />
                  </motion.button>
                  {file.preview && (
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>
                      {formatFileSize(file.file.size)}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Pill bar ── */}
      <div className={`commandbar-focused-wrapper${isFocused || isDragging ? ' is-focused' : ''}${currentAgent === 'eva' ? ' is-eva' : ''}`}>
        <motion.div
          ref={pillRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={focusTextarea}
          animate={{
            height: pillHeight,
            boxShadow: isDragging
              ? '0 0 0 1.5px rgba(109,183,132,0.5), 0 8px 24px rgba(0,0,0,0.2)'
              : isFocused
              ? isLight
                ? (currentAgent === 'eva' ? '0 0 0 1px rgba(201,169,110,0.15), 0 4px 16px rgba(0,0,0,0.05)' : '0 0 0 1px rgba(109,183,132,0.15), 0 4px 16px rgba(0,0,0,0.05)')
                : (currentAgent === 'eva' ? '0 0 0 1px rgba(201,169,110,0.10), 0 8px 32px rgba(0,0,0,0.25)' : '0 0 0 1px rgba(109,183,132,0.10), 0 8px 32px rgba(0,0,0,0.25)')
              : isLight ? '0 2px 8px rgba(0,0,0,0.06)' : '0 8px 32px rgba(0,0,0,0.3)',
          }}
          transition={{ height: { type: 'spring', stiffness: 380, damping: 32 }, boxShadow: { duration: 0.25, ease: 'easeOut' } }}
          className="relative flex items-center gap-2 px-4"
          style={{
            borderRadius: 9999,
            cursor: 'text',
            pointerEvents: 'auto',
            background: isDragging ? 'rgba(109,183,132,0.08)' : isLight ? 'rgba(255,255,255,0.95)' : 'rgba(8,8,10,0.55)',
            border: `1px solid ${isDragging ? 'rgba(109,183,132,0.35)' : isFocused ? (currentAgent === 'eva' ? 'rgba(201,169,110,0.18)' : 'rgba(109,183,132,0.18)') : isLight ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.08)'}`,
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            overflow: 'hidden',
            transition: 'border-color 0.25s ease',
          }}
        >
          <input ref={fileInputRef} type="file" multiple onChange={e => handleFileSelect(e.target.files)} className="hidden" accept="image/*,application/pdf,.doc,.docx,.txt" />

          {/* Paperclip */}
          <motion.button whileHover={{ scale: 1.12 }} whileTap={{ scale: 0.9 }}
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            className="flex items-center justify-center rounded-full flex-shrink-0 cursor-pointer transition-colors"
            style={{ color: isLight ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.45)', pointerEvents: 'auto', minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
            onMouseEnter={e => (e.currentTarget.style.color = isLight ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.75)')}
            onMouseLeave={e => (e.currentTarget.style.color = isLight ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.45)')}
            aria-label="Attach file">
            <i className="ri-attachment-2 text-base leading-none" />
          </motion.button>

          {/* Textarea + fading placeholder wrapper */}
          <div className="flex-1 min-w-0 relative" style={{ alignSelf: 'center' }}>
            <div
              aria-hidden="true"
              style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center',
                pointerEvents: 'none',
                fontSize: 16, lineHeight: '26px',
                fontFamily: "'Inter', system-ui, sans-serif",
                color: isLight ? 'rgba(0,0,0,0.32)' : 'rgba(255,255,255,0.28)',
                opacity: (isFocused || input.length > 0) ? 0 : 1,
                transition: 'opacity 0.2s ease',
                whiteSpace: 'nowrap', overflow: 'hidden',
              }}
            >
              {isDragging ? 'Drop files here…' : (customPlaceholder ?? `Ask ${currentAgentConfig.name} anything…`)}
            </div>
            <textarea ref={textareaRef} value={input} onChange={e => handleInputChange(e.target.value)} onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} onClick={e => e.stopPropagation()}
              rows={1} autoFocus={autoFocus}
              className="w-full bg-transparent resize-none outline-none"
              style={{ color: isLight ? 'var(--color-text-primary)' : 'rgba(255,255,255,0.88)', caretColor: 'var(--color-accent)', fontFamily: "'Inter', system-ui, sans-serif", maxHeight: '120px', paddingTop: '2px', paddingBottom: '2px', lineHeight: '26px', margin: 0, pointerEvents: 'auto', cursor: 'text', zIndex: 1, position: 'relative', WebkitAppearance: 'none', fontSize: 16 }}
            />
          </div>

          {/* Desktop mic button */}
          <div className="hidden sm:flex items-center justify-center flex-shrink-0" style={{ pointerEvents: 'auto' }} onClick={e => e.stopPropagation()}>
            <motion.button
              onClick={(e) => {
                e.stopPropagation();
                const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
                if (!SpeechRecognition) return;
                if (micActive) { recognitionRef.current?.stop(); setMicActive(false); return; }
                const recognition = new SpeechRecognition();
                recognition.continuous = false; recognition.interimResults = true; recognition.lang = 'nl-NL';
                recognition.onstart = () => setMicActive(true);
                recognition.onend = () => setMicActive(false);
                recognition.onerror = () => setMicActive(false);
                recognition.onresult = (event: any) => {
                  const transcript = Array.from(event.results as any[]).map((r: any) => r[0].transcript).join('');
                  setInput(transcript);
                  if (event.results[event.results.length - 1].isFinal) setMicActive(false);
                };
                recognitionRef.current = recognition;
                recognition.start();
              }}
              className="relative w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              animate={{
                background: micActive ? 'rgba(109,183,132,0.14)' : isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)',
                borderColor: micActive ? 'rgba(109,183,132,0.45)' : isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)',
                boxShadow: micActive ? '0 0 14px rgba(109,183,132,0.25), 0 0 4px rgba(109,183,132,0.15)' : 'none',
              }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              style={{ border: '1px solid' }}
              whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.93 }} aria-label="Voice input">
              <AnimatePresence>
                {micActive && (
                  <motion.span key="mic-ring" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: [0.4, 0.15, 0.4], scale: [1, 1.35, 1] }} exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute inset-0 rounded-full" style={{ background: 'rgba(109,183,132,0.18)' }} />
                )}
              </AnimatePresence>
              <motion.i animate={{ color: micActive ? 'rgba(109,183,132,0.95)' : isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.45)' }}
                transition={{ duration: 0.2 }} className="ri-mic-line text-sm leading-none relative z-10" />
            </motion.button>
          </div>

          {/* Send / stop / wave */}
          <div style={{ pointerEvents: 'auto' }} onClick={e => e.stopPropagation()}>
            <motion.button
              whileHover={{ scale: hasContent ? 1.12 : 1.05 }}
              whileTap={{ scale: 0.88 }}
              transition={{ type: 'spring', stiffness: 520, damping: 22 }}
              onClick={(e) => {
                e.stopPropagation();
                if (isStreaming) { onStop?.(); return; }
                if (!hasContent) { onVoiceCall?.(); return; }
                handleSubmit();
              }}
              className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 cursor-pointer relative overflow-hidden"
              style={{ border: '0.5px solid transparent', touchAction: 'manipulation' }}
              animate={{
                background: isStreaming
                  ? (isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.09)')
                  : hasContent
                  ? (isLight ? 'var(--color-accent)' : 'rgba(255,255,255,0.92)')
                  : 'transparent',
                color: isStreaming
                  ? (isLight ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.60)')
                  : hasContent
                  ? (isLight ? '#fff' : 'rgba(12,18,14,0.88)')
                  : (isLight ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.6)'),
                borderColor: (!isStreaming && !hasContent)
                  ? (isLight
                    ? ['rgba(0,0,0,0.10)', 'rgba(0,0,0,0.22)', 'rgba(0,0,0,0.10)']
                    : ['rgba(255,255,255,0.12)', 'rgba(255,255,255,0.28)', 'rgba(255,255,255,0.12)'])
                  : 'rgba(255,255,255,0)',
              }}
              transition={{
                background: { duration: 0.22 },
                color: { duration: 0.18 },
                borderColor: (!isStreaming && !hasContent)
                  ? { duration: 3, repeat: Infinity, ease: 'easeInOut' }
                  : { duration: 0.18 },
              }}
              aria-label={isStreaming ? 'Stop generation' : hasContent ? 'Send' : 'Start voice call'}
            >
              <AnimatePresence mode="wait">
                {isStreaming ? (
                  <motion.i key="stop-s" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.5, opacity: 0 }} transition={{ type: 'spring', stiffness: 480, damping: 26 }} className="ri-stop-fill text-sm leading-none relative z-10" />
                ) : hasContent ? (
                  <motion.i key="send" initial={{ scale: 0.4, opacity: 0, y: 8 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.4, opacity: 0, y: -8 }} transition={{ type: 'spring', stiffness: 480, damping: 26 }} className="ri-arrow-up-line text-sm leading-none font-bold relative z-10" />
                ) : (
                  <motion.div key="wave" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} transition={{ type: 'spring', stiffness: 440, damping: 28 }} className="h-icon relative z-10">
                    <span className="h-bar hb1" /><span className="h-bar hb2" /><span className="h-bar hb3" /><span className="h-bar hb4" /><span className="h-bar hb5" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
}

// ─── Floating CommandBar (kept for potential reuse) ───────────────────────────
interface CommandBarProps {
  onSend: (message: string, files?: File[]) => void;
  mode: string;
  isMobile?: boolean;
  externalInput?: string;
  onExternalInputConsumed?: () => void;
  currentAgent?: AIAgent;
  onAgentChange?: (agent: AIAgent) => void;
  onVoiceCall?: () => void;
}

export function CommandBar({
  onSend,
  mode,
  isMobile = false,
  externalInput = '',
  onExternalInputConsumed,
  currentAgent = 'anna',
  onAgentChange,
  onVoiceCall,
}: CommandBarProps) {
  const mobileStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 'calc(64px + max(0px, env(safe-area-inset-bottom, 0px)) + 0.75rem)',
    left: '0.75rem',
    right: '0.75rem',
    zIndex: 40,
  };
  const desktopStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(720px, calc(100% - 3rem))',
    zIndex: 40,
  };
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 32, delay: 0.1 }}
      style={{ ...(isMobile ? mobileStyle : desktopStyle), paddingBottom: isMobile ? 'max(16px, env(safe-area-inset-bottom))' : 0 }}
    >
      <BarInner mode={mode} isMobile={isMobile} externalInput={externalInput} onExternalInputConsumed={onExternalInputConsumed} onSend={onSend} currentAgent={currentAgent} onAgentChange={onAgentChange} onVoiceCall={onVoiceCall} />
    </motion.div>
  );
}
